import { type PreferenceScore } from '@hungryroot/rest';
import {
  ThumbsDown,
  ThumbsUp,
  ThumbsUpFilled,
  ThumbsDownFilled,
} from '@hungryroot/ui-kit';
import {
  ApiV1LikeDislikeEnum as LikeDislikePairings,
  LikeDislikeTags,
  type LikeDislikeRatings,
  type LikeDislikeTagsRatings,
} from '../BinaryRatings/api';
import styles from './LikeDislike.module.css';

export enum LikeDislikeSize {
  Small,
  Large,
}

export enum LikeDislikeVariant {
  Pairing = 'pairing',
  Tag = 'tag',
  Preference = 'preference',
}

interface LikeDislikeTagsProps {
  variant: LikeDislikeVariant.Tag;
  rating: LikeDislikeTagsRatings;
}

interface LikeDislikePairingsProps {
  variant: LikeDislikeVariant.Pairing;
  rating: LikeDislikeRatings;
}

type LikeDislikePreferencesProps = {
  variant: LikeDislikeVariant.Preference;
  rating: PreferenceScore;
};

type Props = {
  size: LikeDislikeSize;
  ariaLabel?: string;
  testid?: string;
  hideUnselected?: boolean;
  className?: string;
  buttonClassName?: string;
  ratedButtonClassName?: string;
  disabled?: boolean;
  onRating: (
    rating: LikeDislikeTagsRatings | LikeDislikeRatings | PreferenceScore
  ) => void;
} & (
  | LikeDislikeTagsProps
  | LikeDislikePairingsProps
  | LikeDislikePreferencesProps
);

const getRatingEnum = (variant: LikeDislikeVariant) => {
  return variant === LikeDislikeVariant.Pairing
    ? LikeDislikePairings
    : LikeDislikeTags;
};

function renderThumbsUp(props: Props) {
  const {
    rating,
    size,
    hideUnselected,
    buttonClassName: likedislikeButtonClassName,
    ratedButtonClassName,
    variant,
    disabled,
  } = props;
  const LikeDislikeEnum = getRatingEnum(variant);
  if (rating === LikeDislikeEnum.Disliked && hideUnselected === true) {
    return null;
  }

  const { testid, onRating, ariaLabel } = props;
  let buttonClassName =
    size === LikeDislikeSize.Small ? styles.small : styles.large;
  buttonClassName =
    rating === LikeDislikeEnum.Liked
      ? `${buttonClassName} ${styles['thumbsup-filled']} ${ratedButtonClassName}`
      : `${buttonClassName} ${likedislikeButtonClassName}`;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (rating === LikeDislikeEnum.Liked) {
          onRating(LikeDislikeEnum.NoRating);
          return;
        }
        onRating(LikeDislikeEnum.Liked);
      }}
      className={buttonClassName}
      data-testid={`${testid}-thumbsup`}
      aria-label={`like ${ariaLabel} ${
        rating === LikeDislikeEnum.Liked ? 'selected' : 'not selected'
      } `}
      disabled={disabled}
    >
      {rating === LikeDislikeEnum.Liked ? <ThumbsUpFilled /> : <ThumbsUp />}
    </button>
  );
}

function renderThumbsDown(props: Props) {
  const {
    rating,
    size,
    hideUnselected,
    buttonClassName: likedislikeButtonClassName,
    ratedButtonClassName,
    variant,
    disabled,
  } = props;
  const LikeDislikeEnum = getRatingEnum(variant);
  if (rating === LikeDislikeEnum.Liked && hideUnselected === true) {
    return null;
  }

  const { testid, onRating, ariaLabel } = props;
  let buttonClassName =
    size === LikeDislikeSize.Small ? styles.small : styles.large;
  buttonClassName =
    rating === LikeDislikeEnum.Disliked
      ? `${buttonClassName} ${styles['thumbsdown-filled']} ${ratedButtonClassName}`
      : `${buttonClassName} ${likedislikeButtonClassName}`;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (rating === LikeDislikeEnum.Disliked) {
          onRating(LikeDislikeEnum.NoRating);
          return;
        }

        onRating(LikeDislikeEnum.Disliked);
      }}
      className={buttonClassName}
      data-testid={`${testid}-thumbsdown`}
      aria-label={`dislike ${ariaLabel} ${
        rating === LikeDislikeEnum.Disliked ? 'selected' : 'not selected'
      } `}
      disabled={disabled}
    >
      {rating === LikeDislikeEnum.Disliked ? (
        <ThumbsDownFilled />
      ) : (
        <ThumbsDown />
      )}
    </button>
  );
}

function getRatingState(props: Props): string | undefined {
  const { rating, variant } = props;
  const LikeDislikeEnum = getRatingEnum(variant);

  switch (rating) {
    case LikeDislikeEnum.NoRating:
      return `none`;
    case LikeDislikeEnum.Liked:
      return 'liked';
    case LikeDislikeEnum.Disliked:
      return 'disliked';
  }
}

export function LikeDislike(props: Props) {
  const { size, testid, className } = props;
  let containerClassName =
    size === LikeDislikeSize.Small
      ? styles['container-small']
      : styles['container-large'];

  if (className !== undefined) {
    containerClassName = `${containerClassName} ${className}`;
  }

  return (
    <div
      data-rating-state={getRatingState(props)}
      className={containerClassName}
      data-testid={testid}
    >
      {renderThumbsDown(props)}
      {renderThumbsUp(props)}
    </div>
  );
}
